import { useState, useEffect, useCallback, useRef } from 'react'
import { LayoutGrid, LayoutList } from 'lucide-react'
import { useAuth } from '../../../context/AuthContext'
import VideoList from '../stream-manager/VideoList'
import StreamTable from '../stream-manager/StreamTable'
import { Scissors, Play, Clock, Radio, Video, Download, Camera, CheckCircle, AlertCircle, Loader } from 'lucide-react'
import '../stream-manager/StreamManager.css'
import './StreamEditor.css'

export default function StreamEditor() {
  const { api, apiKey } = useAuth()
  const [videos, setVideos] = useState([])
  const [selected, setSelected] = useState(null)
  const [loading, setLoading] = useState(true)
  const [status, setStatus] = useState('')
  const [viewMode, setViewMode] = useState('table')
  const [paging, setPaging] = useState({
    page: 1,
    perPage: 25,
    search: ''
  })

  // Tag filter
  const [allTags, setAllTags] = useState([])
  const [activeTag, setActiveTag] = useState('')
  const [tagFilterUIDs, setTagFilterUIDs] = useState(null)

  // Clip form
  const [clipName, setClipName] = useState('')
  const [startTime, setStartTime] = useState('')
  const [endTime, setEndTime] = useState('')
  const [creating, setCreating] = useState(false)
  const [clipResult, setClipResult] = useState(null)

  // Thumbnail capture
  const [thumbTime, setThumbTime] = useState('')
  const [thumbPreview, setThumbPreview] = useState(null) // blob URL for preview
  const [thumbBlob, setThumbBlob] = useState(null)       // actual blob to upload
  const [thumbName, setThumbName] = useState('')
  const [thumbState, setThumbState] = useState('idle')   // idle | fetching | preview | uploading | done | error
  const [thumbMsg, setThumbMsg] = useState('')
  const [thumbResult, setThumbResult] = useState(null)
  const thumbPreviewRef = useRef(null)

  function isLiveRecording(video) {
    if (!video) return false
    return !!(video.liveInput || video.input?.uid || video.live_input)
  }

  // Load tags
  useEffect(() => {
    api('/tags').then(d => setAllTags(d.tags || [])).catch(() => {})
  }, [api])

  // Tag filter
  useEffect(() => {
    if (!activeTag) {
      setTagFilterUIDs(null)
      return
    }
    api(`/tags/filter?tag=${encodeURIComponent(activeTag)}`)
      .then(d => setTagFilterUIDs(d.stream_uids || []))
      .catch(() => setTagFilterUIDs([]))
  }, [activeTag, api])

  // Load videos
  const loadVideos = useCallback(async () => {
    setLoading(true)
    setStatus('Loading videos...')

    try {
      const params = new URLSearchParams({
        search: paging.search,
        page: paging.page,
        per_page: paging.perPage
      })

      const data = await api(`/videos?${params}`)

      let list = []
      if (Array.isArray(data)) {
        list = data
      } else if (Array.isArray(data.result)) {
        list = data.result
      } else if (data.result?.videos && Array.isArray(data.result.videos)) {
        list = data.result.videos
      } else if (data.videos && Array.isArray(data.videos)) {
        list = data.videos
      } else if (data.data && Array.isArray(data.data)) {
        list = data.data
      }

      setVideos(list)
      setStatus(`${list.length} videos loaded`)
    } catch (e) {
      console.error('Load videos error:', e)
      setStatus(`Error: ${e.message}`)
      setVideos([])
    } finally {
      setLoading(false)
    }
  }, [api, paging])

  useEffect(() => {
    loadVideos()
  }, [loadVideos])

  async function handleSelect(uid) {
    try {
      const data = await api(`/videos/${uid}`)
      const video = data.result || data
      setSelected(video)
      setClipResult(null)
      setClipName(`HIGHLIGHTS - ${video.meta?.name || 'Clip'}`)
      setStartTime('')
      setEndTime('')
      // Reset thumbnail state
      if (thumbPreviewRef.current) URL.revokeObjectURL(thumbPreviewRef.current)
      setThumbTime('')
      setThumbPreview(null)
      setThumbBlob(null)
      setThumbName(`THUMB - ${video.meta?.name || video.uid}`)
      setThumbState('idle')
      setThumbMsg('')
      setThumbResult(null)
    } catch (e) {
      setStatus(`Error: ${e.message}`)
    }
  }

  function handleSearch(term) {
    setPaging(p => ({ ...p, search: term, page: 1 }))
  }

  function handleTagFilter(tag) {
    setActiveTag(tag)
  }

  function handlePageChange(newPage) {
    setPaging(p => ({ ...p, page: newPage }))
  }

  function handlePerPageChange(perPage) {
    setPaging(p => ({ ...p, perPage, page: 1 }))
  }

  function parseTimeToSeconds(t) {
    if (!t) return NaN
    const parts = t.split(':').map(Number)
    if (parts.length === 1) return parts[0]
    if (parts.length === 2) return parts[0] * 60 + parts[1]
    if (parts.length === 3) return parts[0] * 3600 + parts[1] * 60 + parts[2]
    return NaN
  }

  function formatDuration(seconds) {
    if (!seconds) return '0:00'
    const h = Math.floor(seconds / 3600)
    const m = Math.floor((seconds % 3600) / 60)
    const s = Math.floor(seconds % 60)
    if (h > 0) {
      return `${h}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`
    }
    return `${m}:${s.toString().padStart(2, '0')}`
  }

  async function handleStandardClip() {
    const start = parseTimeToSeconds(startTime)
    const end = parseTimeToSeconds(endTime)

    if (isNaN(start) || isNaN(end)) {
      alert('Please enter valid start and end times (formats: seconds, mm:ss, or hh:mm:ss)')
      return
    }

    if (start >= end) {
      alert('End time must be greater than start time')
      return
    }

    if (!clipName.trim()) {
      alert('Please enter a name for the clip')
      return
    }

    setCreating(true)
    setStatus('Creating clip...')
    setClipResult(null)

    try {
      const result = await api('/clip', {
        method: 'POST',
        body: JSON.stringify({
          clippedFromVideoUID: selected.uid,
          startTimeSeconds: start,
          endTimeSeconds: end,
          meta: { name: clipName.trim() }
        })
      })

      setClipResult(result)

      if (result.success !== false) {
        setStatus('✅ Clip created successfully!')
      } else {
        setStatus('❌ Failed to create clip')
      }
    } catch (e) {
      setStatus(`Error: ${e.message}`)
      setClipResult({ error: e.message })
    } finally {
      setCreating(false)
    }
  }

  function handleLiveClip() {
    const start = parseTimeToSeconds(startTime)
    const end = parseTimeToSeconds(endTime)

    if (isNaN(start) || isNaN(end)) {
      alert('Please enter valid start and end times (formats: seconds, mm:ss, or hh:mm:ss)')
      return
    }

    if (start >= end) {
      alert('End time must be greater than start time')
      return
    }

    const duration = end - start

    if (duration > 60) {
      alert('Live instant clips are limited to 60 seconds max. Please shorten your selection or use a duration of 60 seconds or less.')
      return
    }

    const customer = 'customer-bs9kri9klnfqt57u'
    const videoId = selected.uid
    const filename = (clipName.trim() || 'clip').replace(/[^a-zA-Z0-9_-]/g, '_')

    const mp4Url = `https://${customer}.cloudflarestream.com/${videoId}/clip.mp4?time=${start}s&duration=${duration}s&filename=${filename}.mp4`
    const hlsUrl = `https://${customer}.cloudflarestream.com/${videoId}/manifest/clip.m3u8?time=${start}s&duration=${duration}s`

    setClipResult({
      type: 'live_instant',
      success: true,
      clipName: clipName.trim(),
      startTime: start,
      duration: duration,
      mp4Url,
      hlsUrl,
      message: 'Live instant clip generated. Use the links below to preview or download.'
    })
    setStatus('✅ Live instant clip URLs generated!')
  }

  async function handleFetchThumbnail() {
    if (!selected) return
    const seconds = parseTimeToSeconds(thumbTime)
    if (isNaN(seconds) || seconds < 0) {
      alert('Enter a valid timecode (seconds, mm:ss, or hh:mm:ss)')
      return
    }

    setThumbState('fetching')
    setThumbMsg('Fetching frame...')
    setThumbPreview(null)
    setThumbBlob(null)

    try {
      // Fetch through vader proxy to avoid CORS — worker fetches CF Stream server-side
      const API_BASE = import.meta.env.VITE_API_BASE || '/api'
      const res = await fetch(`${API_BASE}/thumbnail/${selected.uid}?time=${seconds}s&width=1280`, {
        headers: { 'X-API-Key': apiKey || '' },
      })

      if (!res.ok) throw new Error(`Could not fetch thumbnail (${res.status})`)

      const blob = await res.blob()
      if (!blob.size) throw new Error('Empty response from thumbnail proxy')

      if (thumbPreviewRef.current) URL.revokeObjectURL(thumbPreviewRef.current)
      const previewUrl = URL.createObjectURL(blob)
      thumbPreviewRef.current = previewUrl

      setThumbBlob(blob)
      setThumbPreview(previewUrl)
      setThumbState('preview')
      setThumbMsg('')
    } catch (e) {
      setThumbState('error')
      setThumbMsg(`Failed to fetch frame: ${e.message}`)
    }
  }

  async function handleSaveThumbnail() {
    if (!thumbBlob || !thumbName.trim()) return

    setThumbState('uploading')
    setThumbMsg('Uploading to Cloudflare Images...')

    try {
      const filename = `${thumbName.trim().replace(/[^a-zA-Z0-9 _-]/g, '')}.jpg`
      const formData = new FormData()
      formData.append('file', thumbBlob, filename)
      formData.append('metadata', JSON.stringify({
        source: 'stream-editor',
        streamUID: selected.uid,
        videoName: selected.meta?.name || selected.uid,
        capturedAt: thumbTime,
      }))

      // Use fetch directly — api() sets JSON content-type which breaks multipart
      const API_BASE = import.meta.env.VITE_API_BASE || '/api'
      const res = await fetch(`${API_BASE}/images/upload`, {
        method: 'POST',
        headers: { 'X-API-Key': apiKey || '' },
        body: formData,
      })

      const data = await res.json()
      if (!data.success) throw new Error(data.errors?.[0]?.message || 'Upload failed')

      setThumbState('done')
      setThumbResult(data.result)
      setThumbMsg(`Saved as "${filename}"`)
      setStatus('✅ Thumbnail saved to Cloudflare Images')
    } catch (e) {
      setThumbState('error')
      setThumbMsg(`Upload failed: ${e.message}`)
    }
  }

  function handleThumbReset() {
    if (thumbPreviewRef.current) URL.revokeObjectURL(thumbPreviewRef.current)
    thumbPreviewRef.current = null
    setThumbPreview(null)
    setThumbBlob(null)
    setThumbState('idle')
    setThumbMsg('')
    setThumbResult(null)
  }

  function handleCreateClip() {
    if (!selected) {
      alert('Please select a video first')
      return
    }

    if (isLiveRecording(selected)) {
      handleLiveClip()
    } else {
      handleStandardClip()
    }
  }

  const playerUrl = selected
    ? `https://customer-bs9kri9klnfqt57u.cloudflarestream.com/${selected.uid}/iframe`
    : null

  // No-op handlers for bulk props (editor doesn't need bulk operations)
  const noop = () => {}
  const emptySet = new Set()

  // Apply tag filter client-side for table mode
  const displayVideos = tagFilterUIDs !== null
    ? videos.filter(v => tagFilterUIDs.includes(v.uid))
    : videos

  // Player block (left side in split mode)
  const playerBlock = selected ? (
    <div className="editor-player">
      <h3>
        <Play size={18} />
        {selected.meta?.name || selected.uid}
        {isLiveRecording(selected) && (
          <span className="badge-live" style={{ marginLeft: 8 }}>LIVE RECORDING</span>
        )}
      </h3>
      <div className="video-player-wrap">
        <iframe
          src={playerUrl}
          allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture"
          allowFullScreen
        />
      </div>
      <div className="video-info">
        <span>Duration: {formatDuration(selected.duration)}</span>
        <span className="mono">UID: {selected.uid}</span>
        <span>Type: {isLiveRecording(selected) ? '🔴 Live Recording' : '📹 Uploaded'}</span>
      </div>

      <div className="thumb-capture">
        <h4><Camera size={14} /> Capture Thumbnail</h4>
        <div className="thumb-capture-row">
          <input
            type="text"
            className="thumb-time-input"
            value={thumbTime}
            onChange={e => setThumbTime(e.target.value)}
            placeholder="Timecode (e.g. 1:23 or 83)"
          />
          <button
            onClick={handleFetchThumbnail}
            disabled={thumbState === 'fetching' || thumbState === 'uploading' || !thumbTime}
            className="thumb-btn"
          >
            {thumbState === 'fetching' ? <Loader size={13} className="spin" /> : <Camera size={13} />}
            {thumbState === 'fetching' ? 'Fetching...' : 'Preview Frame'}
          </button>
        </div>

        {thumbState === 'error' && (
          <div className="thumb-status error"><AlertCircle size={13} /> {thumbMsg}</div>
        )}

        {(thumbState === 'preview' || thumbState === 'uploading' || thumbState === 'done') && thumbPreview && (
          <div className="thumb-preview-area">
            <img src={thumbPreview} alt="Thumbnail preview" className="thumb-preview-img" />
            {thumbState === 'preview' && (
              <>
                <input
                  type="text"
                  className="thumb-name-input"
                  value={thumbName}
                  onChange={e => setThumbName(e.target.value)}
                  placeholder="Image name"
                />
                <div className="thumb-action-row">
                  <button onClick={handleSaveThumbnail} disabled={!thumbName.trim()} className="thumb-btn primary">
                    <Camera size={13} /> Save to Images
                  </button>
                  <button onClick={handleThumbReset} className="thumb-btn secondary">
                    Discard
                  </button>
                </div>
              </>
            )}
            {thumbState === 'uploading' && (
              <div className="thumb-status uploading">
                <Loader size={13} className="spin" /> Uploading to Cloudflare Images...
              </div>
            )}
            {thumbState === 'done' && (
              <>
                <div className="thumb-status done">
                  <CheckCircle size={13} /> {thumbMsg}
                </div>
                {thumbResult?.variants?.[0] && (
                  <a href={thumbResult.variants[0]} target="_blank" rel="noopener noreferrer" className="thumb-link">
                    View in Images ↗
                  </a>
                )}
                <button onClick={handleThumbReset} className="thumb-btn secondary" style={{ marginTop: 8 }}>
                  Capture Another
                </button>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  ) : null

  // Clip form block (right side in split mode)
  const clipBlock = selected ? (
    <div className="clip-creator">
      <h3>
        <Scissors size={18} />
        Create Clip
        {isLiveRecording(selected) && (
          <span style={{ fontSize: 12, fontWeight: 400, color: '#999', marginLeft: 8 }}>
            (Live Instant Clip — max 60s)
          </span>
        )}
      </h3>

      {isLiveRecording(selected) && (
        <div className="live-clip-notice">
          This is a live stream recording. Clips use Cloudflare's instant clipping (max 60 seconds).
          No new video is created — clips are generated dynamically from the recording.
        </div>
      )}

      <div className="clip-form">
        <div className="form-group">
          <label>Clip Name</label>
          <input
            type="text"
            value={clipName}
            onChange={e => setClipName(e.target.value)}
            placeholder="Name for the new clip"
          />
        </div>

        <div className="time-inputs">
          <div className="form-group">
            <label>Start Time</label>
            <input
              type="text"
              value={startTime}
              onChange={e => setStartTime(e.target.value)}
              placeholder="0:00 or 0:00:00"
            />
          </div>
          <div className="form-group">
            <label>End Time</label>
            <input
              type="text"
              value={endTime}
              onChange={e => setEndTime(e.target.value)}
              placeholder="1:30 or 0:01:30"
            />
          </div>
        </div>

        <div className="time-help muted">
          {isLiveRecording(selected)
            ? 'Times are relative to the start of the recording. Max clip duration: 60 seconds.'
            : 'Enter times in seconds (90), mm:ss (1:30), or hh:mm:ss (0:01:30)'
          }
        </div>

        <button
          onClick={handleCreateClip}
          disabled={creating || !clipName || !startTime || !endTime}
          className="create-clip-btn"
        >
          <Scissors size={16} />
          {creating ? 'Creating...' : isLiveRecording(selected) ? 'Generate Clip' : 'Create Clip'}
        </button>
      </div>

      {clipResult && clipResult.type === 'live_instant' && (
        <div className="clip-result success">
          <h4>✅ Live Instant Clip Ready</h4>
          <p style={{ margin: '8px 0', color: '#ccc' }}>{clipResult.message}</p>
          <div className="live-clip-links">
            <a
              href={clipResult.mp4Url}
              target="_blank"
              rel="noopener noreferrer"
              className="clip-link-btn"
            >
              <Download size={14} />
              Download MP4
            </a>
            <a
              href={clipResult.hlsUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="clip-link-btn secondary"
            >
              <Play size={14} />
              HLS Manifest
            </a>
          </div>
          <details style={{ marginTop: 12 }}>
            <summary className="muted" style={{ cursor: 'pointer' }}>Clip URLs</summary>
            <pre className="mono" style={{ fontSize: 11, marginTop: 8 }}>
              MP4: {clipResult.mp4Url}{'\n\n'}HLS: {clipResult.hlsUrl}
            </pre>
          </details>
        </div>
      )}

      {clipResult && clipResult.type !== 'live_instant' && (
        <div className={`clip-result ${clipResult.error ? 'error' : 'success'}`}>
          <h4>{clipResult.error ? '❌ Error' : '✅ Clip Created'}</h4>
          <pre className="mono">
            {JSON.stringify(clipResult, null, 2)}
          </pre>
        </div>
      )}
    </div>
  ) : null

  return (
    <div className="stream-editor">
      <div className="stream-editor-header">
        <h1>Stream Editor</h1>
        <div className="se-header-right">
          <div className="sm-view-toggle">
            <button
              className={`sm-view-toggle-btn ${viewMode === 'cards' ? 'active' : ''}`}
              onClick={() => setViewMode('cards')}
              title="Card view"
            >
              <LayoutGrid size={16} />
            </button>
            <button
              className={`sm-view-toggle-btn ${viewMode === 'table' ? 'active' : ''}`}
              onClick={() => setViewMode('table')}
              title="Table view"
            >
              <LayoutList size={16} />
            </button>
          </div>
          <span className="status-text">{status}</span>
        </div>
      </div>

      <div className={`stream-editor-layout ${viewMode === 'table' ? 'se-layout-table' : 'se-layout-cards'}`}>
        {viewMode === 'cards' ? (
          <>
            <div className="stream-manager-sidebar">
              <VideoList
                videos={videos}
                selected={selected}
                loading={loading}
                paging={paging}
                onSelect={handleSelect}
                onSearch={handleSearch}
                onPageChange={handlePageChange}
                onPerPageChange={handlePerPageChange}
                allTags={allTags}
                activeTag={activeTag}
                onTagFilter={handleTagFilter}
                tagFilterUIDs={tagFilterUIDs}
                checked={emptySet}
                onToggle={noop}
                onToggleAll={noop}
                onClearSelection={noop}
                onBulkTag={noop}
                onBulkRequestDownloads={noop}
                onBulkArchive={noop}
                onBulkDelete={noop}
                onBulkCaptions={noop}
                bulkBusy={false}
              />
            </div>

            <div className="editor-main-panel">
              {selected ? (
                <>
                  {playerBlock}
                  {clipBlock}
                </>
              ) : (
                <div className="no-selection">
                  <Scissors size={48} />
                  <h3>Select a Video to Edit</h3>
                  <p>Select a video from the list to create clips</p>
                </div>
              )}
            </div>
          </>
        ) : (
          <>
            {/* Table mode: split detail top, filters + table bottom */}
            <div className="se-detail-panel">
              {selected ? (
                <div className="se-detail-split">
                  <div className="se-detail-split-left">
                    {playerBlock}
                  </div>
                  <div className="se-detail-split-right">
                    {clipBlock}
                  </div>
                </div>
              ) : (
                <div className="no-selection">
                  <Scissors size={48} />
                  <h3>Select a Video to Edit</h3>
                  <p>Select a video from the table below to create clips</p>
                </div>
              )}
            </div>

            {/* Filters bar */}
            <div className="sm-filters-bar">
              <form className="sm-filter-search" onSubmit={e => { e.preventDefault(); handleSearch(e.target.querySelector('input').value) }}>
                <input type="text" placeholder="Search videos..." defaultValue={paging.search} />
                <button type="submit">Search</button>
              </form>

              <div className="sm-filter-tag">
                <select value={activeTag} onChange={e => handleTagFilter(e.target.value)}>
                  <option value="">All Tags</option>
                  {(allTags || []).map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>

              <div className="sm-filter-pagination">
                <select value={paging.perPage} onChange={e => handlePerPageChange(Number(e.target.value))}>
                  <option value={25}>25/pg</option>
                  <option value={50}>50/pg</option>
                  <option value={100}>100/pg</option>
                </select>
                <button onClick={() => handlePageChange(paging.page - 1)} disabled={paging.page <= 1}>‹</button>
                <span>Pg {paging.page}</span>
                <button onClick={() => handlePageChange(paging.page + 1)}>›</button>
              </div>
            </div>

            <StreamTable
              videos={displayVideos}
              loading={loading}
              selected={selected}
              onSelect={handleSelect}
              checked={emptySet}
              onToggle={noop}
              onToggleAll={noop}
              vodLinks={{}}
            />
          </>
        )}
      </div>
    </div>
  )
}
